import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { ErrorBox } from '@/components/ui/misc';
import { auth } from '@/lib/auth';
import { setupQuery } from '@/lib/queries';
import { cn, IS_MOCK } from '@/lib/utils';
import { AuthLayout } from './auth-layout';

type Step = 1 | 2 | 3;

export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export function QrCanvas({ value }: { value: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current)
      void QRCode.toCanvas(ref.current, value, {
        margin: 2,
        width: 200,
        color: { dark: '#0f0f0e', light: '#e8e6e1' },
      });
  }, [value]);
  return <canvas ref={ref} role="img" aria-label="TOTP QR code" className="rounded-sm" />;
}

export function SetupPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setup = useQuery(setupQuery);
  const [setupToken, setSetupToken] = useState('');
  const [step, setStep] = useState<Step>(1);
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', org: '', slug: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [totp, setTotp] = useState<{ totpURI: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [passkeyDone, setPasskeyDone] = useState(false);

  if (setup.isPending)
    return (
      <AuthLayout title={t('setup.title')}>
        <Spinner />
      </AuthLayout>
    );
  if (!started && setup.data && !setup.data.needsSetup) return <Navigate to="/login" search={{}} />;

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === 'org' && !slugTouched ? { slug: slugify(v) } : {}) }));
  };

  const submitAccount = (e: FormEvent) => {
    e.preventDefault();
    void guard(async () => {
      setStarted(true);
      await auth.signUpEmail({
        name: form.name,
        email: form.email,
        password: form.password,
        setupToken,
        organizationName: form.org,
        organizationSlug: form.slug,
      });
      // The protected setup creates the organization with the chosen name and slug.
      const enrolled = await auth.enableTotp(form.password);
      setTotp(enrolled);
      setStep(2);
    });
  };

  const submitTotp = (e: FormEvent) => {
    e.preventDefault();
    void guard(async () => {
      await auth.verifyTotp({ code: code.trim() });
      setStep(3);
    });
  };

  const finish = async () => {
    await qc.invalidateQueries();
    void navigate({ to: '/chat' });
  };

  const steps = (
    <ol className="flex gap-2 text-2xs" aria-label="Progress">
      {[t('setup.step1'), t('setup.step3'), t('setup.step4')].map((label, i) => (
        <li
          key={label}
          className={cn(i + 1 === step ? 'text-accent' : i + 1 < step ? 'text-success' : 'text-muted')}
          aria-current={i + 1 === step ? 'step' : undefined}
        >
          {i + 1 < step ? '✓' : `${i + 1}.`} {label}
        </li>
      ))}
    </ol>
  );

  return (
    <AuthLayout title={t('setup.title')} steps={steps}>
      {step === 1 ? (
        <form onSubmit={submitAccount} className="flex flex-col gap-3">
          <Field label="Setup token" htmlFor="s-token">
            <Input
              id="s-token"
              type="password"
              required
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field label={t('setup.name')} htmlFor="s-name">
            <Input
              id="s-name"
              prompt
              autoFocus
              required
              autoComplete="name"
              value={form.name}
              onChange={set('name')}
            />
          </Field>
          <Field label={t('auth.email')} htmlFor="s-email">
            <Input
              id="s-email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={set('email')}
            />
          </Field>
          <Field label={t('auth.password')} htmlFor="s-pw" hint={t('setup.passwordHint')}>
            <Input
              id="s-pw"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={form.password}
              onChange={set('password')}
            />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
            <Field label={t('setup.orgName')} htmlFor="s-org">
              <Input id="s-org" required value={form.org} onChange={set('org')} placeholder="Acme Labs" />
            </Field>
            <Field label={t('setup.orgSlug')} htmlFor="s-slug">
              <Input
                id="s-slug"
                required
                pattern="[a-z0-9][a-z0-9-]{1,39}"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set('slug')(e);
                }}
              />
            </Field>
          </div>
          <ErrorBox error={error} />
          <Button type="submit" variant="primary" loading={busy} className="justify-center">
            {t('setup.next')} →
          </Button>
        </form>
      ) : null}

      {step === 2 && totp ? (
        <form onSubmit={submitTotp} className="flex flex-col gap-3">
          <p className="text-sm">{t('setup.scanQr')}</p>
          <div className="flex justify-center">
            <QrCanvas value={totp.totpURI} />
          </div>
          <details className="text-xs text-muted">
            <summary className="cursor-pointer">{t('setup.manualKey')}</summary>
            <code className="mt-1 block break-all text-fg">{totp.totpURI}</code>
          </details>
          <Field label="TOTP" htmlFor="s-totp">
            <Input
              id="s-totp"
              prompt
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={IS_MOCK ? '123456' : '••••••'}
              className="tracking-[0.4em]"
            />
          </Field>
          <div className="rounded-sm border border-border p-2">
            <p className="mb-1 text-xs text-warn">{t('setup.backupCodes')}</p>
            <ul className="grid grid-cols-2 gap-x-4 text-sm tabular">
              {totp.backupCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
          <ErrorBox error={error} />
          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={code.trim().length < 6}
            className="justify-center"
          >
            {t('auth.verify')}
          </Button>
        </form>
      ) : null}

      {step === 3 ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted">{t('setup.passkeyHint')}</p>
          {passkeyDone ? <p className="text-sm text-success">✓ Passkey added</p> : null}
          <ErrorBox error={error} />
          <div className="flex flex-wrap justify-between gap-2">
            <Button
              variant="outline"
              loading={busy}
              disabled={passkeyDone}
              onClick={() =>
                void guard(async () => {
                  await auth.addPasskey(`${form.name || 'Owner'}'s device`);
                  setPasskeyDone(true);
                })
              }
            >
              ⚿ {t('setup.addPasskey')}
            </Button>
            <Button variant="primary" onClick={() => void finish()}>
              {passkeyDone ? t('setup.done') : t('setup.skip')} →
            </Button>
          </div>
        </div>
      ) : null}
    </AuthLayout>
  );
}
